import React, { FocusEvent, MouseEvent } from "react";
import clsx from "clsx";
import {
    encodeUnicode,
    getEntryUrl,
    HtmlReactParser,
    stripTags,
    translate,
    useMediaQuery,
} from "@kaltura/mediaspace-shared-utils";
import { useTheme } from "@kaltura/mediaspace-shared-styled";
import { BackgroundThumbnail } from "@kaltura/mediaspace-shared-ui";
import { formatEntryThumbnailUrl } from "@kaltura/mediaspace-shared-utils";
import { Button, Tooltip } from "@kaltura/ds-react-components";
import { ButtonClickAnalyticsType, KmsTypePresenter, StaticImage } from "@kaltura/mediaspace-shared-types";
import { PreviewPlayer } from "@kaltura/mediaspace-shared-player";
import { Lines } from "./Lines";
import { EventPresentersList } from "@kaltura/mediaspace-shared-event-platform-event-list";
import {
    ButtonsWrapper,
    Content,
    Description,
    LiveLabel,
    Name,
    StyledBackDrop,
    StyledHeroUnit,
    Title,
    TitleWrapper,
    TogglePlayButton,
} from "./styled-components";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import TruncateMarkup from "react-truncate-markup";

export interface HeroUnitProps {
    /**
     * additional classname to add to the root div
     */
    className?: string;

    /**
     * current entry id, required when not showing static images
     */
    id?: string;

    /**
     * current entry name, used in entry links
     */
    entryName?: string;

    /**
     * current entry/item name to be displayed
     */
    name?: string;

    isLive: boolean;

    /**
     * page title to show above item details
     */
    title?: string;

    presenters?: KmsTypePresenter[];
    description?: string;
    currentItemIndex: number;
    mediaCount: number;
    entryCarouselInterval: number;
    currentTime: number;

    /**
     * did user toggle playback on
     */
    userSelectedIsActive: boolean;
    /**
     * is playback active:
     * the playback may be inactive
     * either because the user decided to turn it off
     * or because the carousel is focused or hovered
     */
    isActive: boolean;
    playerMedia: { entryId: string; ks?: string };
    customizedPlayerConfig: Record<string, unknown>;
    // eslint-disable-next-line
    onPlayerReadyHandler: (playerInstance: any) => void;
    toggleIsActiveClickHandler: (ev: React.MouseEvent<HTMLButtonElement>) => void;
    onLineClick: (args: { index: number }) => void;

    /**
     * image to show while player playback unavailable
     */
    thumbnailUrl?: string;

    /**
     * current static image info.
     * Static images gets priority over entries. Decision about which type to display (images or entries)
     * should be done on server side.
     */
    staticImage?: StaticImage;

    /**
     * names of all items in carousel
     */
    itemsNames?: string[];

    onFocus: () => void;
    onBlur: () => void;
    onMouseEnter: () => void;
    onMouseLeave: () => void;
}

export const HeroUnit: React.FC<HeroUnitProps> = ({
    className,
    id,
    entryName,
    name = "",
    isLive,
    entryCarouselInterval,
    title,
    onLineClick,
    presenters = [],
    currentTime,
    description,
    currentItemIndex,
    mediaCount,
    userSelectedIsActive,
    isActive,
    playerMedia,
    customizedPlayerConfig,
    toggleIsActiveClickHandler,
    onPlayerReadyHandler,
    thumbnailUrl = "",
    staticImage,
    itemsNames = [],
    onFocus,
    onBlur,
    onMouseEnter,
    onMouseLeave,
}) => {
    const theme = useTheme();
    const smallScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const isMobileLandscape = window.innerWidth < 960 && window.matchMedia("(orientation: landscape)").matches;

    const playButtonText = !staticImage ? translate("Play Preview") : translate("Play carousel");
    const pauseButtonText = !staticImage ? translate("Pause Preview") : translate("Pause carousel");

    const sendButtonAnalytics = useButtonAnalytics();

    const heroLink = !staticImage ? getEntryUrl(id!, undefined, entryName) : staticImage?.actionUrl;

    const handleHeroClick = (event: MouseEvent<HTMLElement>) => {
        if (!heroLink) {
            return;
        }
        const target = event.target as HTMLInputElement;
        const parent = target.parentElement as HTMLInputElement;
        if (target.type === "button" || parent.type === "button") {
            sendButtonAnalytics(`Hero - ${isLive ? "Live Now" : "Watch Now"}`, ButtonClickAnalyticsType.NAVIGATE);
        }
        else {
            sendButtonAnalytics("Hero - Media Selected", ButtonClickAnalyticsType.THUMBNAIL);
        }

        document.location.href = heroLink;
    };

    /*
     * Focusing any element in the carousel stops the playback (according to the a11y guidelines),
     * but the "toggle playback" button is an exception.
     * That's why the focus/blur events of this button shouldn't be propagated to the container element's listener.
     */
    const stopBlurPropagation = (ev: FocusEvent<HTMLButtonElement>) => {
        ev.stopPropagation();
    };

    return (
        <StyledHeroUnit
            className={clsx("kms-ds-homepage-component-hero-unit", className)}
            tabIndex={-1}
            data-analytics={encodeUnicode(name)}
            onClick={heroLink ? handleHeroClick : undefined}
            onFocus={onFocus}
            onBlur={onBlur}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            isMobileLandscape={isMobileLandscape}
            isClickable={!!heroLink}
        >
            {!staticImage ? (
                <PreviewPlayer
                    className={"kms-ds-homepage-component-hero-unit-preview"}
                    playerClassName={"kms-ds-homepage-component-hero-unit-player"}
                    active={isActive}
                    media={playerMedia}
                    playerConfig={customizedPlayerConfig}
                    onReady={onPlayerReadyHandler}
                >
                    <BackgroundThumbnail
                        thumbnailUrl={formatEntryThumbnailUrl(thumbnailUrl, "auto", "auto")}
                        className={"kms-ds-homepage-component-hero-unit-img"}
                    />
                </PreviewPlayer>
            ) : (
                <BackgroundThumbnail
                    thumbnailUrl={formatEntryThumbnailUrl(staticImage.imageUrl, "auto", "auto")}
                    className={"kms-ds-homepage-component-hero-unit-img"}
                    alt={staticImage.alt}
                />
            )}

            <StyledBackDrop className="kms-ds-homepage-component-hero-unit-backdrop" />
            {!!title && (
                <TitleWrapper isMobileLandscape={isMobileLandscape}>
                    <TruncateMarkup lines={isMobileLandscape ? 1 : 3}>
                        <Title variant={"h1"} className={"kms-ds-homepage-component-hero-unit-title"}>
                            {HtmlReactParser(title)}
                        </Title>
                    </TruncateMarkup>
                </TitleWrapper>
            )}
            {/* as it is rendered inside link element, component needs to have application role
             in order put screen reader into 'forms' mode and enable the interactive buttons to work properly*/}
            <Content role="application" className="kms-ds-homepage-component-hero-unit-content">
                {isLive && <LiveLabel>{translate("Live")}</LiveLabel>}

                {!!name && (
                    <Tooltip title={name}>
                        <Name variant={!smallScreen ? "h1" : "h2"} component={title ? "h2" : "h1"}>
                            <span className="kms-ds-homepage-component-hero-unit-name">
                                <TruncateMarkup lines={isMobileLandscape ? 1 : 2}>
                                    <span>{name}</span>
                                </TruncateMarkup>
                            </span>
                        </Name>
                    </Tooltip>
                )}

                {!isLive && !!description && (
                    <Description className={"kms-ds-homepage-component-hero-unit-description"}>
                        <TruncateMarkup lines={isMobileLandscape ? 2 : 4}>
                            <span>{stripTags(description)}</span>
                        </TruncateMarkup>
                    </Description>
                )}

                {isLive && (
                    <EventPresentersList
                        presenters={presenters}
                        isTranslucent={true}
                        className={"kms-ds-homepage-component-hero-unit-presenters"}
                        analyticsDescription={"Hero unit"}
                        entryId={id}
                    />
                )}

                <ButtonsWrapper>
                    {!staticImage ? (
                        <Tooltip title={translate("Visit media page %1", [name])}>
                            <Button
                                className={"kms-ds-homepage-component-hero-unit-join-button"}
                                href={getEntryUrl(id!, undefined, entryName)}
                                color={isLive ? "danger" : "primary"}
                                type={"button"}
                                size={"large"}
                            >
                                {isLive ? translate("Join Now") : translate("Watch Now")}
                            </Button>
                        </Tooltip>
                    ) : (
                        staticImage.actionUrl && (
                            <Button href={staticImage.actionUrl} color={"primary"} size={"large"}>
                                {staticImage.actionText}
                            </Button>
                        )
                    )}

                    <TogglePlayButton
                        type={"button"}
                        variant={"borderless"}
                        color={"translucent"}
                        className={"kms-ds-homepage-component-hero-unit-toggle-play-button"}
                        onClick={(ev: MouseEvent<HTMLButtonElement>) => {
                            sendButtonAnalytics(
                                `Hero - ${userSelectedIsActive ? "Pause" : "Play"} Preview`,
                                ButtonClickAnalyticsType.TOGGLE
                            );
                            toggleIsActiveClickHandler(ev);
                        }}
                        aria-label={
                            userSelectedIsActive
                                ? translate("Stop playing carousel preview")
                                : translate("Start playing carousel preview")
                        }
                        onFocus={stopBlurPropagation}
                        onBlur={stopBlurPropagation}
                    >
                        {userSelectedIsActive ? pauseButtonText : playButtonText}
                    </TogglePlayButton>
                </ButtonsWrapper>
            </Content>

            {mediaCount > 1 && (
                <Lines
                    className="kms-ds-homepage-component-hero-unit-lines"
                    currentItemIndex={currentItemIndex}
                    onLineClick={onLineClick}
                    currentTime={currentTime}
                    entryCarouselInterval={entryCarouselInterval}
                    itemsNames={itemsNames}
                />
            )}
        </StyledHeroUnit>
    );
};

export default HeroUnit;
